import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Video from 'twilio-video'

import { generalNotifications } from 'components/common/Notification/GeneralNotification'
import { checklistErrorTypes, checklistStatusTypes } from 'constants/checklist'
import { checklistEventEmitter, checklistKeys } from 'helpers/events'
import { dispatchSetChecklistStatus } from 'store/dispatcher'
import { _userType } from 'store/selectors/User'
import { dispatchError } from 'store/thunks/Checklist'
import { isDesktop } from 'utils/device'
import { logger } from 'utils/logger'
import { networkSpeedStore, NETWORK_LOWER_THRESHOLD } from 'utils/networkSpeed'

import { constraints } from './mediaConstraints'

export default function useLocalTracks() {
    const [audioTrack, setAudioTrack] = useState()
    const [videoTrack, setVideoTrack] = useState()
    const [dataTrack, setDataTrack] = useState()
    const [isAcquiringLocalTracks, setIsAcquiringLocalTracks] = useState(false)

    const userType = useSelector(_userType)
    const getLocalAudioTrack = useCallback(
        deviceId => {
            const options = {
                ...constraints(userType).audio,
            }

            if (deviceId) {
                options.deviceId = { exact: [deviceId] }
            }

            return Video.createLocalAudioTrack(options).then(newTrack => {
                setAudioTrack(newTrack)
                return newTrack
            })
        },
        [userType]
    )

    const getLocalVideoTrack = useCallback(
        newOptions => {
            // In the DeviceSelector and FlipCameraButton components, a new video track is created,
            // then the old track is unpublished and the new track is published. Unpublishing the old
            // track and publishing the new track at the same time sometimes causes a conflict when the
            // track name is 'camera', so here we append a timestamp to the track name to avoid the
            // conflict.
            const options = {
                name: `camera-${Date.now()}`,
                ...constraints(userType).video,
                ...newOptions,
            }

            return Video.createLocalVideoTrack(options).then(newTrack => {
                setVideoTrack(newTrack)
                return newTrack
            })
        },
        [userType]
    )

    const getLocalDataTrack = useCallback(() => {
        const dataTrack = Video.LocalDataTrack()
        setDataTrack(dataTrack)
        return dataTrack
    }, [])

    useEffect(() => {
        const createLocalTracks = async () => {
            try {
                setIsAcquiringLocalTracks(true)

                const { video, audio } = constraints(userType)

                try {
                    if (networkSpeedStore.get() < NETWORK_LOWER_THRESHOLD) {
                        generalNotifications.notifyLowBandwidth({
                            title: 'Can’t see yourself on this call?',
                            subtitle:
                                'Your internet connection seems weak. We’ve turned off your video for a better experience.',
                        })
                    } else {
                        const videoTrack = await Video.createLocalVideoTrack(
                            video
                        )
                        videoTrack && setVideoTrack(videoTrack)
                    }
                } catch (error) {
                    logger.error({
                        msg: 'twilio video track creation failed',
                        error,
                    })
                    generalNotifications.notifyVideoTrackError()
                }

                try {
                    const audioTrack = await Video.createLocalAudioTrack(audio)
                    audioTrack && setAudioTrack(audioTrack)
                } catch (error) {
                    logger.error({
                        msg: 'twilio audio track creation failed',
                        error,
                    })
                    dispatchError(checklistErrorTypes.AUDIO_VIDEO)
                }

                const dataTrack = Video.LocalDataTrack()
                dataTrack && setDataTrack(dataTrack)
                dispatchSetChecklistStatus(checklistStatusTypes.SUCCESS)
            } catch (error) {
                logger.error({ msg: `Twilio track creation failed.`, error })
            } finally {
                setIsAcquiringLocalTracks(false)
            }
        }

        checklistEventEmitter.addListener(
            checklistKeys.AV_PERMISSION,
            createLocalTracks
        )

        return () =>
            checklistEventEmitter.removeListener(
                checklistKeys.AV_PERMISSION,
                createLocalTracks
            )
    }, [userType])

    useEffect(() => {
        const handleStopped = () => setVideoTrack(undefined)
        if (videoTrack) {
            videoTrack.on('stopped', handleStopped)
            return () => {
                videoTrack.off('stopped', handleStopped)
            }
        }
    }, [videoTrack])

    useEffect(() => {
        const handleStopped = async () => {
            if (isDesktop) {
                try {
                    await audioTrack.restart({
                        deviceId: { exact: 'default' },
                    })
                } catch (err) {
                    logger.error(`error in switching audio input :- ${err}`)
                }
            } else {
                setAudioTrack(undefined)
            }
        }
        const handleStarted = newAudioTrack => setAudioTrack(newAudioTrack)

        if (audioTrack) {
            audioTrack.on('stopped', handleStopped)
            audioTrack.on('started', handleStarted)
            return () => {
                audioTrack.off('stopped', handleStopped)
                audioTrack.on('started', handleStarted)
            }
        }
    }, [audioTrack])

    const localTracks = [audioTrack, videoTrack, dataTrack].filter(
        track => track !== undefined
    )

    return {
        audioTrack,
        videoTrack,
        dataTrack,
        localTracks,
        getLocalVideoTrack,
        getLocalAudioTrack,
        getLocalDataTrack,
        isAcquiringLocalTracks,
    }
}
